import React, { useEffect, useMemo, useState } from 'react';
import ClosedTradeItem, { ClosedTradeCard } from './ClosedTradeItem';
import { useTranslation } from 'react-i18next';

const MOBILE_QUERY = '(max-width: 639px)';
const isMobileScreen = () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;

// Phones render fewer rows per batch (and only the card list, not the hidden table) to stay light on weak devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(isMobileScreen);
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

export default function PaginatedJournalClosedTradesTable({
  listData = {} as any,
  sortBy = '',
  searchTradingJounal = '',
}) {
  const isMobile = useIsMobile();
  const pageSize = isMobile ? 10 : 20;
  const [visibleRows, setVisibleRows] = useState(pageSize);

  // Start from the first batch again when the search, sort or screen size changes
  useEffect(() => {
    setVisibleRows(pageSize);
  }, [searchTradingJounal, sortBy, pageSize]);

  // Sort + filter only when the inputs change (the report object is refreshed often).
  // Copy before sorting: Array.sort mutates the shared report data used by other sections.
  const trades = listData?.journal_closed_trades;
  const filteredSortedData = useMemo(() => [...(trades || [])]
    .sort((a: any, b: any) => {
      if (sortBy === 'close_time_desc') {
        return new Date(b.close_time).getTime() - new Date(a.close_time).getTime();
      } else if (sortBy === 'close_time_asc') {
        return new Date(a.close_time).getTime() - new Date(b.close_time).getTime();
      }
      return 0;
    })
    .filter((item: any) => {
      const q = searchTradingJounal.toLowerCase();
      // String(): history_id can be a number, and `number.toLowerCase` would throw
      return String(item.symbol ?? '').toLowerCase().includes(q) ||
        String(item.side ?? '').toLowerCase().includes(q) ||
        String(item.history_id ?? '').toLowerCase().includes(q);
    }), [trades, sortBy, searchTradingJounal]);

  // Paginate the data
  const dataToShow = filteredSortedData?.slice(0, visibleRows);

  // Check if there are more items to load
  const hasMore = filteredSortedData?.length > visibleRows;

  // "Xem thêm" handler
  const handleLoadMore = () => {
    setVisibleRows((prev) => prev + pageSize);
  };
  const { t } = useTranslation();

  return (
    <>
      {/* Phones: one compact card per closed trade (net profit always visible) */}
      {isMobile ? (
      <div className="divide-y divide-slate-100">
        {dataToShow?.map((item: any, index: any) => (
          <ClosedTradeCard key={index} data={item} />
        ))}
      </div>
      ) : (
      <table className="_table_11ccc_13">
        <thead>
          <tr>
            <th colSpan={1} style={{ width: '160px', minWidth: '160px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">{t('CloseTime')}</div>
            </th>
            <th colSpan={1} style={{ width: '140px', minWidth: '120px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">{t('Symbol')}</div>
            </th>
            <th colSpan={1} style={{ width: '100px', minWidth: '90px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">{t('Side')}</div>
            </th>
            <th colSpan={1} style={{ width: '100px', minWidth: '90px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">{t('Volume')}</div>
            </th>
            <th colSpan={1} style={{ width: '140px', minWidth: '120px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">{t('NetProfit')}</div>
            </th>
            {/* <th colSpan={1} style={{ width: '120px', minWidth: '100px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">Commission</div>
            </th>
            <th colSpan={1} style={{ width: '120px', minWidth: '100px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">Swap</div>
            </th> */}
            <th colSpan={1} style={{ width: '140px', minWidth: '120px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">{t('GrossProfit')}</div>
            </th>
            <th colSpan={1} style={{ width: '160px', minWidth: '140px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">{t('BalanceAfter')}</div>
            </th>
            <th colSpan={1} style={{ width: '100px', minWidth: '90px' }}>
              <div className="select-none text-headings-headline text-content-tertiary">Pips</div>
            </th>
          </tr>
        </thead>
        <tbody style={{ maxHeight: '1000px', overflowY: 'scroll' }}>
          {dataToShow?.map((item: any, index: any) => (
            <ClosedTradeItem key={index} data={item} />
          ))}
        </tbody>
      </table>
      )}
      {/* Load More Button */}
      {hasMore && (
        <div className='hide-mod' style={{ textAlign: 'center', margin: '16px 0' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleLoadMore}
          >
            {t('Loadmore')} ({dataToShow.length}/{filteredSortedData.length})
          </button>
        </div>
      )}
    </>
  );
}